import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SafeImg from '@/components/SafeImg';
import { motion } from 'framer-motion';
import { 
  Play, Pause, ArrowLeft, Music, Zap, 
  Key, Clock, Gauge, Volume2, ChevronDown, ChevronUp, ListMusic, Plus,
  SkipBack, SkipForward, Rewind, FastForward, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Waveform from '../components/Waveform';

export default function TrackDetailPage() {
  const { id } = useParams();
  const [track, setTrack] = useState(null);
  const [compatibleTracks, setCompatibleTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showStructure, setShowStructure] = useState(true);
  const [showCompatible, setShowCompatible] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playingCompatibleId, setPlayingCompatibleId] = useState(null);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const compatibleAudioRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [trackRes, compatibleRes, playlistsRes] = await Promise.all([
          axios.get(`${API}/tracks/${id}`),
          axios.get(`${API}/tracks/${id}/compatible`),
          axios.get(`${API}/playlists?include_suggested=false`)
        ]);
        setTrack(trackRes.data.track);
        setCompatibleTracks(compatibleRes.data.compatible_tracks || []);
        setPlaylists(playlistsRes.data.playlists || []);

        // Silent iTunes backfill — if the track has no cover yet, try
        // once per session to fetch it from iTunes so the detail page
        // shows real artwork instead of the CRATELAB placeholder cube.
        // sessionStorage guard prevents repeated lookups for tracks
        // that genuinely have no match on iTunes.
        const t = trackRes.data.track;
        if (t && !t.cover_url && !t.artwork_url) {
          const tried = sessionStorage.getItem(`coverTried:${t.id}`);
          if (!tried) {
            sessionStorage.setItem(`coverTried:${t.id}`, '1');
            try {
              const { data } = await axios.post(`${API}/tracks/${t.id}/cover/backfill`);
              if (data?.cover_url) {
                setTrack((prev) => prev ? { ...prev, cover_url: data.cover_url } : prev);
              }
            } catch (_e) {
              // Silent — manual "Auto-fetch cover" button stays available.
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch track');
        toast.error('Track not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => {
      setIsPlaying(true);
      // Stop compatible track if playing
      if (compatibleAudioRef.current) {
        compatibleAudioRef.current.pause();
        setPlayingCompatibleId(null);
      }
    };
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [track?.id]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => console.error('Play error:', e));
      }
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
    }
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    audioRef.current.currentTime = percent * duration;
  };

  const handleStructureClick = (segment) => {
    if (audioRef.current && segment.start !== undefined) {
      audioRef.current.currentTime = segment.start;
    }
  };

  const playCompatibleTrack = (trackId) => {
    // Stop main track if playing
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
    }

    if (playingCompatibleId === trackId) {
      // Stop this track
      if (compatibleAudioRef.current) {
        compatibleAudioRef.current.pause();
      }
      setPlayingCompatibleId(null);
    } else {
      // Play new track
      if (compatibleAudioRef.current) {
        compatibleAudioRef.current.pause();
      }
      compatibleAudioRef.current = new Audio(`${API}/tracks/${trackId}/audio`);
      compatibleAudioRef.current.play();
      compatibleAudioRef.current.onended = () => setPlayingCompatibleId(null);
      setPlayingCompatibleId(trackId);
    }
  };

  const addToPlaylist = async (playlistId, trackIdToAdd = id) => {
    try {
      await axios.post(`${API}/playlists/${playlistId}/tracks/${trackIdToAdd}`);
      toast.success('Added to playlist');
    } catch (e) {
      toast.error('Failed to add to playlist');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!track) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Music className="w-16 h-16 text-[#1E3A8A] mb-4" />
        <p className="font-heading text-xl text-[#64748B]">Track not found</p>
        <Link to="/library" className="text-[#2563FF] hover:text-[#3B82F6] mt-4">
          Back to Library
        </Link>
      </div>
    );
  }

  const analysis = track.analysis || {};
  const structureData = analysis.structure?.map((seg, i) => ({
    name: `${seg.type || 'Seg'} ${i + 1}`,
    energy: seg.energy,
    type: seg.type,
    start: seg.start,
    duration: seg.duration,
  })) || [];

  // Calculate structure bar positions for visualization
  const structureBars = analysis.structure?.map((seg, i) => {
    const startPercent = (seg.start / track.duration) * 100;
    const widthPercent = (seg.duration / track.duration) * 100;
    return { ...seg, startPercent, widthPercent, index: i };
  }) || [];

  const getStructureColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'intro': return 'bg-blue-500';
      case 'buildup': return 'bg-yellow-500';
      case 'drop': return 'bg-[#2563FF]';
      case 'breakdown': return 'bg-purple-500';
      case 'outro': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen py-8 px-4">
      {/* Hidden audio element */}
      <audio ref={audioRef} src={`${API}/tracks/${track.id}/audio`} preload="metadata" />

      <div className="max-w-6xl mx-auto">
        <Link 
          to="/library" 
          className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-8 font-heading text-sm"
          data-testid="back-to-library"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to library
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Track info & player */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-pane p-6"
            >
              <div className="aspect-square rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] mb-6 relative overflow-hidden border border-[#E2E8F0] group">
                {track.cover_url ? (
                  <SafeImg src={track.cover_url} alt={track.title} className="w-full h-full object-cover" iconClassName="w-24 h-24 text-[#94A3B8]" />
                ) : track.artwork_url ? (
                  <SafeImg src={track.artwork_url} alt={track.title} className="w-full h-full object-cover" iconClassName="w-24 h-24 text-[#94A3B8]" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center opacity-40">
                    <img src="/icon-512.png" alt="CRATELAB" className="w-32 h-32 object-contain" />
                  </div>
                )}
                {!track.cover_url && !track.artwork_url && (
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/tracks/${track.id}/cover/backfill`);
                        if (data?.cover_url) {
                          setTrack((t) => ({ ...t, cover_url: data.cover_url }));
                          toast.success('Cover art fetched from iTunes');
                        } else {
                          toast.message('No match found on iTunes');
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Backfill failed');
                      }
                    }}
                    className="absolute bottom-3 right-3 px-3 py-1.5 rounded-md bg-[#2563FF] text-white text-xs font-semibold hover:bg-[#1D4ED8] shadow-lg flex items-center gap-1.5"
                    data-testid="track-cover-backfill"
                    title="Look up cover art on iTunes"
                  >
                    <Sparkles className="w-3 h-3" />
                    Auto-fetch cover
                  </button>
                )}
              </div>

              <h1 className="font-heading text-2xl font-bold text-[#0F172A] mb-1" data-testid="track-title">
                {track.title}
              </h1>
              <p className="font-body text-[#64748B] mb-2">{track.artist}</p>
              {track.album && <p className="font-body text-[#64748B] text-sm mb-1">{track.album}</p>}
              {track.year && <p className="font-mono text-[#94A3B8] text-xs mb-4">{track.year}</p>}

              <div className="flex items-center gap-4 mb-6 font-mono text-sm text-[#64748B]">
                {track.genre && <span className="badge-data">{track.genre}</span>}
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDuration(track.duration)}
                </span>
              </div>

              {/* Player Controls */}
              <div className="mb-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <button onClick={() => skipTime(-10)} className="p-2 text-[#64748B] hover:text-[#0F172A]" title="Back 10s">
                    <Rewind className="w-5 h-5" />
                  </button>
                  <button onClick={togglePlay} className="p-4 gradient-primary text-white rounded-full shadow-[0_8px_20px_rgba(37,99,255,0.35)] hover:shadow-[0_12px_28px_rgba(37,99,255,0.45)] transition-all hover:scale-105" data-testid="play-button">
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                  </button>
                  <button onClick={() => skipTime(10)} className="p-2 text-[#64748B] hover:text-[#0F172A]" title="Forward 10s">
                    <FastForward className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Progress bar with structure */}
                <div 
                  ref={progressRef}
                  onClick={handleProgressClick}
                  className="h-8 rounded-lg bg-[#F1F5F9] cursor-pointer relative mb-2 overflow-hidden"
                  data-testid="progress-bar"
                >
                  {/* Structure segments */}
                  {structureBars.map((seg, i) => (
                    <div
                      key={i}
                      className={`absolute top-0 h-full opacity-70 hover:opacity-100 cursor-pointer ${getStructureColor(seg.type)}`}
                      style={{ left: `${seg.startPercent}%`, width: `${seg.widthPercent}%` }}
                      onClick={(e) => { e.stopPropagation(); handleStructureClick(seg); }}
                      title={`${seg.type || 'Section'} - ${formatDuration(seg.start)}`}
                    />
                  ))}
                  {/* Progress indicator */}
                  <div 
                    className="absolute top-0 h-full bg-white/40 pointer-events-none"
                    style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                  />
                  {/* Current position marker */}
                  <div 
                    className="absolute top-0 h-full w-0.5 bg-[#2563FF] pointer-events-none shadow-[0_0_8px_rgba(37,99,255,0.6)]"
                    style={{ left: `${(currentTime / duration) * 100 || 0}%` }}
                  />
                </div>
                
                <div className="flex justify-between text-xs font-mono text-[#64748B]">
                  <span>{formatDuration(currentTime)}</span>
                  <span>{formatDuration(duration)}</span>
                </div>
              </div>

              {/* Structure legend */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="flex items-center gap-1 text-xs font-mono text-[#64748B]">
                  <div className="w-3 h-3 rounded-sm bg-blue-500" /> Intro
                </span>
                <span className="flex items-center gap-1 text-xs font-mono text-[#64748B]">
                  <div className="w-3 h-3 rounded-sm bg-yellow-500" /> Build
                </span>
                <span className="flex items-center gap-1 text-xs font-mono text-[#64748B]">
                  <div className="w-3 h-3 rounded-sm bg-[#2563FF]" /> Drop
                </span>
                <span className="flex items-center gap-1 text-xs font-mono text-[#64748B]">
                  <div className="w-3 h-3 rounded-sm bg-purple-500" /> Break
                </span>
                <span className="flex items-center gap-1 text-xs font-mono text-[#64748B]">
                  <div className="w-3 h-3 rounded-sm bg-green-500" /> Outro
                </span>
              </div>

              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full btn-secondary flex items-center justify-center gap-2" data-testid="add-to-playlist-btn">
                    <ListMusic className="w-5 h-5" />
                    Add to playlist
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#E2E8F0]">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-[#0F172A]">Add to Playlist</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-2 pt-4 max-h-60 overflow-y-auto">
                    {playlists.length > 0 ? playlists.map(pl => (
                      <button
                        key={pl.id}
                        onClick={() => addToPlaylist(pl.id)}
                        className="w-full text-left p-3 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/50 hover:bg-[#F8FAFC] text-[#0F172A] font-body transition-colors"
                      >
                        {pl.name}
                        <span className="font-mono text-xs text-[#64748B] ml-2">
                          ({pl.active_track_count ?? (pl.track_ids?.length || 0)} tracks)
                        </span>
                      </button>
                    )) : (
                      <p className="text-[#64748B] text-center py-4">No playlists yet</p>
                    )}
                  </div>
                  <Link to="/playlists" className="text-[#2563FF] hover:text-[#3B82F6] text-[15px] text-center block mt-4">
                    Create a playlist first →
                  </Link>
                </DialogContent>
              </Dialog>
            </motion.div>
          </div>

          {/* Right: Analysis data */}
          <div className="lg:col-span-2 space-y-6">
            {/* Key metrics with Camelot */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-3"
            >
              <div className="p-5 rounded-xl border border-[#E2E8F0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="bpm-display">
                <div className="flex items-center gap-2 mb-2">
                  <Gauge className="w-5 h-5 text-[#2563FF]" />
                  <span className="font-mono text-xs text-[#64748B] uppercase tracking-widest">BPM</span>
                </div>
                <p className="font-mono text-3xl font-bold text-[#0F172A] neon-glow">
                  {analysis.bpm?.toFixed(0) || '--'}
                </p>
              </div>

              <div className="p-5 rounded-xl border border-[#E2E8F0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="key-display">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-5 h-5 text-[#2563FF]" />
                  <span className="font-mono text-xs text-[#64748B] uppercase tracking-widest">KEY</span>
                </div>
                <p className="font-mono text-xl font-bold text-[#0F172A]">{analysis.key || '--'}</p>
                <p className="font-mono text-2xl font-bold text-[#2563FF] neon-glow mt-1" data-testid="camelot-display">
                  {analysis.camelot || '--'}
                </p>
              </div>

              <div className="p-5 rounded-xl border border-[#E2E8F0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="energy-display">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-[#2563FF]" />
                  <span className="font-mono text-xs text-[#64748B] uppercase tracking-widest">ENERGY</span>
                </div>
                <p className="font-mono text-3xl font-bold text-[#0F172A]">{analysis.energy?.toFixed(0) || '--'}</p>
                <div className="mt-3 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div className="h-full energy-bar" style={{ width: `${Math.min(analysis.energy || 0, 100)}%` }} />
                </div>
              </div>

              <div className="p-5 rounded-xl border border-[#E2E8F0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="kick-energy-display">
                <div className="flex items-center gap-2 mb-2">
                  <Volume2 className="w-5 h-5 text-[#2563FF]" />
                  <span className="font-mono text-xs text-[#64748B] uppercase tracking-widest">KICK</span>
                </div>
                <p className="font-mono text-3xl font-bold text-[#0F172A]">{analysis.kick_energy?.toFixed(0) || '--'}</p>
                <div className="mt-3 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div className="h-full energy-bar" style={{ width: `${Math.min(analysis.kick_energy || 0, 100)}%` }} />
                </div>
              </div>
            </motion.div>

            {/* Structure Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden"
            >
              <button
                onClick={() => setShowStructure(!showStructure)}
                className="w-full p-4 flex items-center justify-between hover:bg-[#F1F5F9] transition-colors"
              >
                <span className="font-heading text-lg font-bold">Track structure</span>
                {showStructure ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showStructure && structureData.length > 0 && (
                <div className="p-6 pt-0">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={structureData}>
                      <defs>
                        <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563FF" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#2563FF" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" stroke="#94A3B8" tick={{ fill: '#94A3B8', fontSize: 12 }} />
                      <YAxis stroke="#94A3B8" tick={{ fill: '#94A3B8', fontSize: 12 }} domain={[0, 100]} />
                      <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', color: '#0F172A', borderRadius: '12px', boxShadow: '0 8px 24px rgba(15,23,42,0.08)' }} />
                      <Area type="monotone" dataKey="energy" stroke="#2563FF" strokeWidth={2} fill="url(#energyGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </motion.div>

            {/* Audio Waveform — always render so structure preview shows even without playable audio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="glass-pane p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Music className="w-5 h-5 text-[#2563FF]" />
                <span className="font-heading text-lg font-bold">Audio waveform</span>
              </div>
              <Waveform
                audioUrl={track.audio_url || null}
                height={120}
                waveColor="#CBD5E1"
                progressColor="#2563FF"
                cursorColor="#14E6C9"
                structure={analysis.structure || []}
              />
            </motion.div>

            {/* Drops & Cue Points */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-pane p-6"
              >
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#2563FF]" />
                  Drops
                </h3>
                {analysis.drops?.length > 0 ? (
                  <div className="space-y-2">
                    {analysis.drops.map((drop, i) => (
                      <button 
                        key={i} 
                        onClick={() => { if (audioRef.current) audioRef.current.currentTime = drop; }}
                        className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#2563FF]/50 hover:bg-[#EFF6FF] transition-colors"
                      >
                        <span className="font-mono text-sm text-[#64748B]">Drop {i + 1}</span>
                        <span className="font-mono text-lg text-[#2563FF] neon-glow">{formatDuration(drop)}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#64748B] text-sm">No drops detected</p>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-pane p-6"
              >
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Music className="w-5 h-5 text-[#2563FF]" />
                  Cue points
                </h3>
                {analysis.cue_points?.length > 0 ? (
                  <div className="space-y-2">
                    {analysis.cue_points.map((cue, i) => (
                      <button 
                        key={i} 
                        onClick={() => { if (audioRef.current) audioRef.current.currentTime = cue.time; }}
                        className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#2563FF]/50 hover:bg-[#EFF6FF] transition-colors"
                      >
                        <div>
                          <span className="font-mono text-sm text-[#0F172A]">{cue.label}</span>
                          <span className="ml-2 badge-data text-xs">{cue.type}</span>
                        </div>
                        <span className="font-mono text-lg text-[#2563FF]">{formatDuration(cue.time)}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#64748B] text-sm">No cue points detected</p>
                )}
              </motion.div>
            </div>

            {/* Compatible Tracks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden"
            >
              <button
                onClick={() => setShowCompatible(!showCompatible)}
                className="w-full p-4 flex items-center justify-between hover:bg-[#F1F5F9] transition-colors"
              >
                <span className="font-heading text-lg font-bold">
                  Compatible tracks
                  <span className="ml-2 font-mono text-sm text-[#2563FF]">({compatibleTracks.length})</span>
                </span>
                {showCompatible ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showCompatible && (
                <div className="p-6 pt-0">
                  {compatibleTracks.length > 0 ? (
                    <div className="space-y-3">
                      {compatibleTracks.slice(0, 8).map((t) => (
                        <div 
                          key={t.id} 
                          className={`p-4 rounded-xl border transition-colors flex items-center gap-4 ${
                            playingCompatibleId === t.id 
                              ? 'border-[#2563FF] bg-[#EFF6FF]' 
                              : 'border-[#E2E8F0] hover:border-[#2563FF]/50'
                          }`}
                        >
                          {/* Play button */}
                          <button
                            onClick={() => playCompatibleTrack(t.id)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              playingCompatibleId === t.id 
                                ? 'gradient-primary text-white shadow-[0_4px_12px_rgba(37,99,255,0.35)]' 
                                : 'bg-[#EFF6FF] text-[#2563FF] hover:bg-[#2563FF] hover:text-white'
                            }`}
                            data-testid={`play-compatible-${t.id}`}
                          >
                            {playingCompatibleId === t.id ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                          </button>
                          
                          {/* Track info */}
                          <Link to={`/track/${t.id}`} className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`badge-data text-xs ${
                                t.compatibility === 'perfect' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                t.compatibility === 'excellent' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                ''
                              }`}>
                                {t.compatibility}
                              </span>
                              <span className="font-mono text-xs text-[#64748B]">±{t.bpm_diff} BPM</span>
                            </div>
                            <p className="font-normal text-[#3B82F6] text-[15px] truncate hover:text-[#2563FF] transition-colors">{t.title}</p>
                            <p className="font-body text-[#64748B] text-xs truncate">{t.artist}</p>
                          </Link>
                          
                          {/* Camelot & BPM */}
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <div className="text-center">
                              <span className="font-mono text-[#2563FF] font-bold">{t.analysis?.camelot}</span>
                            </div>
                            <div className="text-center">
                              <span className="font-mono text-[#0F172A] text-sm">{t.analysis?.bpm?.toFixed(0)}</span>
                            </div>
                          </div>
                          
                          {/* Add to playlist */}
                          <Dialog>
                            <DialogTrigger asChild>
                              <button 
                                className="p-2 text-[#64748B] hover:text-[#2563FF] transition-colors"
                                title="Add to playlist"
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            </DialogTrigger>
                            <DialogContent className="bg-white border-[#E2E8F0]">
                              <DialogHeader>
                                <DialogTitle className="font-heading text-[#0F172A]">Add "{t.title}" to Playlist</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-2 pt-4 max-h-60 overflow-y-auto">
                                {playlists.length > 0 ? playlists.map(pl => (
                                  <button
                                    key={pl.id}
                                    onClick={() => addToPlaylist(pl.id, t.id)}
                                    className="w-full text-left p-3 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/50 hover:bg-[#F8FAFC] text-[#0F172A] font-body transition-colors"
                                  >
                                    {pl.name}
                                    <span className="font-mono text-xs text-[#64748B] ml-2">
                                      ({pl.active_track_count ?? (pl.track_ids?.length || 0)} tracks)
                                    </span>
                                  </button>
                                )) : (
                                  <p className="text-[#64748B] text-center py-4">No playlists yet</p>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#64748B] text-sm text-center py-4">
                      No compatible tracks found. Upload more tracks with similar keys!
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
